import React, { Fragment, memo, useCallback, useRef, useState } from 'react'
import Button from '@material-ui/core/Button'
import GridList from '@material-ui/core/GridList'
import GridListTile from '@material-ui/core/GridListTile'
import GridListTileBar from '@material-ui/core/GridListTileBar'
import IconButton from '@material-ui/core/IconButton'
import LinearProgress from '@material-ui/core/LinearProgress'
import ListItemIcon from '@material-ui/core/ListItemIcon'
import ListItemText from '@material-ui/core/ListItemText'
import MoreIcon from '@material-ui/icons/MoreHorizOutlined'
import Tooltip from '@material-ui/core/Tooltip'
import { withStyles } from '@material-ui/core'
import clsx from 'clsx'
import { AmplifyS3Image } from '@aws-amplify/ui-react'
import { StyledMenu, StyledMenuItem } from '../StyledMenu'
import DeleteIcon from '@material-ui/icons/DeleteOutlineOutlined'
import DownloadIcon from '@material-ui/icons/CloudDownloadOutlined'
import EditIcon from '@material-ui/icons/EditOutlined'
import ViewIcon from '@material-ui/icons/LaunchOutlined'
import { ConfirmDelete } from '../confirmation/Confirm'
import { ATTACHMENTS_MAX, MAX_LENGTH_ATTACHMENT_NAME } from '../../data/limits'
import RenameDialog from '../RenameDialog'
import { isMobile } from 'react-device-detect'

const MAX_ATTACHMENTS_MESSAGE = 'No more attachments can be added to this item'

// use withStyles rather than makeStyles as don't know how to let parent override styles otherwise
const styles = theme => ({
  root: {
    display: 'flex',
    flexWrap: 'wrap',
    justifyContent: 'space-around',
    overflow: 'hidden',
    backgroundColor: theme.palette.background.paper
  },
  margin: {},
  fullWidth: {},
  icon: {
    color: 'white'
  },
  gridList: {
    width: '100%'
  }
})

const getAttachmentTypeFromMIME = mimetype => {
  if (mimetype) {
    const type = mimetype.split('/')[0]
    return type
  }
  return null
}

export default withStyles(styles)(
  memo(
    ({
      classes,
      createAttachment,
      deleteAttachment,
      editAttachment,
      downloadAttachment,
      attachments
    }) => {
      const attachmentInputRef = useRef()

      const [uploadProgress, setUploadProgress] = useState(null)
      const [contextAttachment, setContextAttachment] = useState(null)

      const hasMaxAttachments = useCallback(
        () => attachments.length >= ATTACHMENTS_MAX,
        [attachments]
      )

      const handleSelectImage = e => {
        e.preventDefault()

        setUploadProgress(0)

        createAttachment(
          e.target.files[0],
          percent => {
            setUploadProgress(percent)
          },
          () => setUploadProgress(null)
        )

        // In case we want to select the same file again, e.g. add, delete, add
        e.target.value = null
      }

      const handleMenuClick = useCallback((event, attachment) => {
        event.stopPropagation()
        setContextAttachment({ anchorEl: event.currentTarget, attachment })
      }, [])

      const handleMenuClose = useCallback(() => {
        setContextAttachment(previous => {
          return { ...previous, anchorEl: null }
        })
      }, [])

      const handleRename = useCallback(() => {
        if (contextAttachment) {
          setContextAttachment(previous => {
            return { ...previous, openRename: true }
          })
          handleMenuClose()
        }
      }, [contextAttachment, handleMenuClose])

      const handleDelete = useCallback(
        event => {
          if (contextAttachment) {
            setContextAttachment(previous => {
              return { ...previous, openConfirmDelete: true }
            })
            handleMenuClose()
          }
        },
        [contextAttachment, handleMenuClose]
      )

      const handleAttachmentClick = useCallback(
        attachment => {
          downloadAttachment(
            attachment,
            isMobile
              ? false
              : getAttachmentTypeFromMIME(attachment.type) === 'image'
          )
        },
        [downloadAttachment]
      )

      const handleDownload = useCallback(
        (event, viewInTab = false) => {
          if (contextAttachment) {
            downloadAttachment(contextAttachment.attachment, viewInTab)
            handleMenuClose()
          }
        },
        [contextAttachment, handleMenuClose, downloadAttachment]
      )

      return (
        <Fragment>
          <div className={clsx(classes.margin, classes.fullWidth)}>
            <div style={{ height: 10 }}>
              {uploadProgress !== null && (
                <LinearProgress
                  variant='determinate'
                  value={uploadProgress}
                  style={{ width: '100%' }}
                />
              )}
            </div>

            {attachments && attachments.length > 0 && (
              <div className={classes.root}>
                <GridList className={classes.gridList} cellHeight='auto'>
                  {attachments.map(attachment => (
                    <GridListTile key={attachment.id}>
                      <div style={{ minHeight: 48 }}>
                        {getAttachmentTypeFromMIME(attachment.type) ===
                          'image' && (
                          // minHeight - In case attachment deleted
                          <div
                            style={{ cursor: 'pointer' }}
                            onClick={() => handleAttachmentClick(attachment)}
                          >
                            <AmplifyS3Image
                              level='private'
                              imgKey={attachment.key}
                              style={{
                                '--width': '100%'
                              }}
                            />
                          </div>
                        )}
                        <GridListTileBar
                          title={attachment.name}
                          actionIcon={
                            <IconButton
                              aria-label={`info about ${attachment.name}`}
                              className={classes.icon}
                              onClick={e => handleMenuClick(e, attachment)}
                            >
                              <MoreIcon />
                            </IconButton>
                          }
                        />
                      </div>
                    </GridListTile>
                  ))}
                </GridList>
              </div>
            )}
            <br />
            <input
              id='attachmentInput'
              type='file'
              ref={ref => (attachmentInputRef.current = ref)}
              style={{ display: 'none' }}
              onChange={handleSelectImage}
            />
            <Tooltip title={hasMaxAttachments() ? MAX_ATTACHMENTS_MESSAGE : ''}>
              {/* Need span to have a child disabled button */}
              <span>
                <Button
                  variant='contained'
                  disabled={hasMaxAttachments()}
                  color='primary'
                  label='Add Attachment'
                  onClick={() => attachmentInputRef.current.click()}
                >
                  Add Attachment
                </Button>
              </span>
            </Tooltip>
          </div>
          <StyledMenu
            id='attachment-menu'
            anchorEl={contextAttachment ? contextAttachment.anchorEl : null}
            open={
              contextAttachment ? Boolean(contextAttachment.anchorEl) : false
            }
            onClose={handleMenuClose}
          >
            <StyledMenuItem onClick={handleRename}>
              <ListItemIcon>
                <EditIcon fontSize='small' />
              </ListItemIcon>
              <ListItemText primary='Rename' />
            </StyledMenuItem>
            {isMobile && (
              <StyledMenuItem onClick={e => handleDownload(e, false)}>
                <ListItemIcon>
                  <ViewIcon fontSize='small' />
                </ListItemIcon>
                <ListItemText primary='Open' />
              </StyledMenuItem>
            )}
            {!isMobile && (
              <StyledMenuItem onClick={e => handleDownload(e, true)}>
                <ListItemIcon>
                  <ViewIcon fontSize='small' />
                </ListItemIcon>
                <ListItemText primary='View' />
              </StyledMenuItem>
            )}
            {!isMobile && (
              <StyledMenuItem onClick={e => handleDownload(e, false)}>
                <ListItemIcon>
                  <DownloadIcon fontSize='small' />
                </ListItemIcon>
                <ListItemText primary='Download' />
              </StyledMenuItem>
            )}

            <StyledMenuItem onClick={handleDelete}>
              <ListItemIcon>
                <DeleteIcon fontSize='small' />
              </ListItemIcon>
              <ListItemText primary='Delete' />
            </StyledMenuItem>
          </StyledMenu>
          {contextAttachment && (
            <ConfirmDelete
              open={Boolean(contextAttachment.openConfirmDelete)}
              deleteItem={contextAttachment.attachment.name}
              onOk={async () => {
                deleteAttachment(contextAttachment.attachment)
                setContextAttachment(previous => {
                  return { ...previous, openConfirmDelete: false }
                })
              }}
              onCancel={() =>
                setContextAttachment(previous => {
                  return { ...previous, openConfirmDelete: false }
                })
              }
            />
          )}
          {contextAttachment && (
            <RenameDialog
              open={Boolean(contextAttachment.openRename)}
              title='Rename Attachment'
              defaultName={contextAttachment.attachment.name}
              placeholder={contextAttachment.attachment.filename}
              maxLength={MAX_LENGTH_ATTACHMENT_NAME}
              onOk={async name => {
                editAttachment(contextAttachment.attachment, name)
                setContextAttachment(previous => {
                  return { ...previous, openRename: false }
                })
              }}
              onCancel={() =>
                setContextAttachment(previous => {
                  return { ...previous, openRename: false }
                })
              }
            />
          )}
        </Fragment>
      )
    }
  )
)
